import { MenuItem, Select, TextField } from '@mui/material'
import { useState } from 'react'
import { Close, ContentCopyRounded } from '@mui/icons-material'
import Modal from 'react-modal'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faCar, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { formatDateTimeInUruguay } from './utils'
import LoadingIndicator from '../../common/LoadingIndicator'
import formatPrice from '../../utils/formatPrice'
import Alert from '../../common/Alert'
import { PreLobbyInfo } from './UserLobbies'
import { JoinCompanion } from './UserActions'
import RefundPill, { describeRefundReason, getRefundInfo } from '../../common/RefundPill'
import { walletShareLabel } from '../../utils/walletPayment'
import usePermissions from '../../hooks/usePermissions'
import RefundDialog from '../../common/RefundDialog'
import ResolveRefundDialog from '../../common/ResolveRefundDialog'
import { isManualRefund, refundAfterResolve } from '../../common/ResolveRefundDialog/utils'
import RefundRemainderDialog from '../../common/RefundRemainderDialog'
import { isRemainderRefundable, refundAfterRemainder } from '../../common/RefundRemainderDialog/utils'
import { formatMoney, isRefundable, paymentMethodLabel } from '../../common/RefundDialog/utils'

export default function UserTransactions({ transactionsVisible, setTransactionsVisible, userId }) {
  const { loading, data } = useFetch(`${urlApi}/admin/users/payments/${userId}`)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLobby, setSelectedLobby] = useState({})
  const [tab, setTab] = useState('completed')

  const transactions = data?.transactions || []
  const pendingTransactions = data?.pending_transactions || []

  const filteredTransactions = transactions.filter(transaction =>
    transaction._id.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const customStyles = {
    content: {
      borderRadius: '1.5rem',
      marginTop: 'auto',
      marginBottom: 'auto',
      paddingLeft: '1.6rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    overlay: {
      zIndex: 90,
      backgroundColor: 'rgba(0,0,0,0.5)'
    }
  }

  return (
    <>
      <Modal
        isOpen={transactionsVisible}
        onRequestClose={() => setTransactionsVisible(false)}
        style={customStyles}
      >
        <div className='flex flex-col h-full overflow-hidden'>
          <div className='flex justify-between items-center mb-4'>
            <div className='flex gap-2'>
              <button
                onClick={() => setTab('completed')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  tab === 'completed'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-dk-raised dark:text-gray-300 dark:hover:bg-dk-hover'
                }`}
              >
                Transacciones Realizadas
              </button>
              <button
                onClick={() => setTab('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  tab === 'pending'
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-dk-raised dark:text-gray-300 dark:hover:bg-dk-hover'
                }`}
              >
                Transacciones Pendientes
                {pendingTransactions.length > 0 && (
                  <span className='ml-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full'>
                    {pendingTransactions.length}
                  </span>
                )}
              </button>
            </div>
            <Close onClick={() => setTransactionsVisible(false)} className='cursor-pointer' />
          </div>

          {tab === 'completed' && (
            <>
              <div className='mb-4'>
                <TextField
                  label='Buscar por ID'
                  variant='outlined'
                  size='small'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='w-1/2'
                  placeholder='Ingresa el ID de la transacción'
                />
              </div>
              {loading ? (
                <div className='flex h-full items-center justify-center'>
                  <LoadingIndicator />
                </div>
              ) : filteredTransactions.length > 0 ? (
                <div className='flex flex-col'>
                  {searchTerm && (
                    <p className='text-sm text-gray dark:text-gray-400 mb-2'>
                      Mostrando {filteredTransactions.length} de {transactions.length} transacciones
                    </p>
                  )}
                  <ol className='overflow-auto h-[calc(100vh-14rem)]'>
                    {filteredTransactions.map((transaction, index) => (
                      <li key={transaction.id || Math.random()}>
                        <TransactionCard
                          transaction={transaction}
                          index={index}
                          transactions={filteredTransactions}
                          onSelectLobby={lobbyId => setSelectedLobby({ _id: lobbyId })}
                        />
                      </li>
                    ))}
                    <li className='text-xl text-center mb-4'>FIN</li>
                  </ol>
                </div>
              ) : transactions.length > 0 ? (
                <div className='flex justify-center'>
                  <p>No se encontraron transacciones con ese ID</p>
                </div>
              ) : (
                <div className='flex justify-center'>
                  <p>No hay transacciones</p>
                </div>
              )}
            </>
          )}

          {tab === 'pending' && (
            <>
              {loading ? (
                <div className='flex h-full items-center justify-center'>
                  <LoadingIndicator />
                </div>
              ) : pendingTransactions.length > 0 ? (
                <div className='overflow-auto h-[calc(100vh-10rem)]'>
                  {pendingTransactions.map(transaction => (
                    <TransactionCard
                      key={transaction._id}
                      transaction={transaction}
                      onSelectLobby={lobbyId => setSelectedLobby({ _id: lobbyId })}
                      isPending
                    />
                  ))}
                  <p className='text-xl text-center mb-4'>FIN</p>
                </div>
              ) : (
                <div className='flex justify-center'>
                  <p>No hay transacciones pendientes</p>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {Object.keys(selectedLobby).length !== 0 && (
        <PreLobbyInfo lobbyId={selectedLobby._id} setSelectedLobby={setSelectedLobby} />
      )}
    </>
  )
}

function TransactionCard({ transaction, onSelectLobby, isPending }) {
  const [joinCompanionAlert, setJoinCompanionAlert] = useState(false)
  const [joinCompanion, setJoinCompanion] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('wire-transfer')
  const [status, setStatus] = useState(transaction.status)
  const [refund, setRefund] = useState(null)
  const [refundDialog, setRefundDialog] = useState(false)
  const [resolveDialog, setResolveDialog] = useState(false)
  const [remainderDialog, setRemainderDialog] = useState(false)
  const { hasPermission } = usePermissions()

  const copyPaymentLink = () => {
    const currency = transaction.currency || 'ARS'
    const link = `https://www.mercadopago.com.${
      currency === 'UYU' ? 'uy' : 'ar'
    }/checkout/v1/redirect?preference-id=${transaction.preference_id}`
    navigator.clipboard.writeText(link)
  }

  const formatSafeDate = dateValue => {
    if (!dateValue) return null
    try {
      return formatDateTimeInUruguay(dateValue)
    } catch (error) {
      console.warn('Invalid date value:', dateValue, error)
      return null
    }
  }

  const purchaseDate = formatSafeDate(transaction.purchase_date)
  const refundDate = formatSafeDate(refund?.date_created ?? transaction.refund_date)

  const statusConfig = {
    approved: {
      label: 'Aprobado',
      bg: 'bg-green-50 dark:bg-emerald-500/10',
      text: 'text-green-700 dark:text-emerald-400',
      border: 'border-green-200 dark:border-emerald-500/30'
    },
    refunded: {
      label: 'Reembolsado',
      bg: 'bg-red-50 dark:bg-red-500/10',
      text: 'text-red-700 dark:text-red-400',
      border: 'border-red-200 dark:border-red-500/30'
    },
    pending: {
      label: 'Pendiente',
      bg: 'bg-yellow-50 dark:bg-amber-500/10',
      text: 'text-yellow-700 dark:text-amber-400',
      border: 'border-yellow-200 dark:border-amber-500/30'
    },
    authorized: {
      label: 'Autorizado',
      bg: 'bg-green-50 dark:bg-emerald-500/10',
      text: 'text-green-700 dark:text-emerald-400',
      border: 'border-green-200 dark:border-emerald-500/30'
    },
    cancelled: {
      label: 'Cancelado',
      bg: 'bg-red-50 dark:bg-red-500/10',
      text: 'text-red-700 dark:text-red-400',
      border: 'border-red-200 dark:border-red-500/30'
    }
  }

  const statusInfo = statusConfig[status] || {
    label: status,
    bg: 'bg-gray-50 dark:bg-dk-raised',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-200 dark:border-dk-border'
  }

  const transactionWithStatus = refund ? { ...transaction, status, refund } : { ...transaction, status }
  const refundInfo = getRefundInfo(transactionWithStatus)
  const hasRefund = Boolean(refundInfo)
  const refundReason = refundInfo ? describeRefundReason(refundInfo) : null
  const showStatusPill = !(status === 'refunded' && hasRefund)
  const walletShare = walletShareLabel({
    walletAmount: transaction.wallet_amount,
    total: transaction.price,
    currency: transaction.currency
  })
  const canRefund = !isPending && !hasRefund && isRefundable({ status }) && hasPermission('edit-lobby')
  const canResolveRefund = !isPending && isManualRefund(refundInfo) && hasPermission('edit-lobby')
  const canRefundRemainder = !isPending && isRemainderRefundable(refundInfo, status) && hasPermission('edit-lobby')

  const formatShortDate = dateObj =>
    dateObj ? `${dateObj.day}/${dateObj.month}/${dateObj.year} ${dateObj.time}` : null

  return (
    <>
      <div className='mb-3'>
        <div className='rounded-xl border border-gray/30 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow dark:border-dk-border dark:bg-dk-surface'>
          <div className='flex items-center justify-between px-4 py-3 bg-light_gray/50 dark:bg-dk-raised/50'>
            <div className='flex flex-wrap items-center gap-2'>
              {showStatusPill && (
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                >
                  {statusInfo.label}
                </span>
              )}
              <RefundPill transaction={transactionWithStatus} />
              {refundReason && (
                <span className='text-xs font-medium text-gray-600 dark:text-gray-400'>{refundReason}</span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              {walletShare && (
                <span className='bg-cyan-50 text-cyan-800 border border-cyan-200 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap dark:bg-cyan-500/10 dark:text-cyan-300 dark:border-cyan-500/30'>
                  {walletShare}
                </span>
              )}
              {transaction.coupon && (
                <span className='bg-green-50 text-green-700 border border-green-200 text-xs font-semibold px-2 py-0.5 rounded-full dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30'>
                  {transaction.coupon}
                </span>
              )}
              {transaction.discount ? (
                <>
                  <span className='text-sm text-gray line-through dark:text-gray-400'>
                    ${formatPrice(transaction.price + transaction.discount)}
                  </span>
                  <span className='text-lg font-bold'>${formatPrice(transaction.price)}</span>
                </>
              ) : (
                <span className='text-lg font-bold'>${formatPrice(transaction.price)}</span>
              )}
            </div>
          </div>

          <div className='px-4 py-3 flex flex-col gap-3'>
            <div className='flex items-center gap-2 text-sm'>
              <div className='flex flex-col items-center gap-0.5'>
                <span className='w-2 h-2 rounded-full bg-primary' />
                <span className='w-px h-4 bg-gray dark:bg-dk-border' />
                <span className='w-2 h-2 rounded-full bg-primary' />
              </div>
              <div className='flex flex-col gap-1 min-w-0'>
                <span className='font-medium truncate'>{transaction.origin}</span>
                <span className='truncate text-gray-600 dark:text-gray-300'>{transaction.destination}</span>
              </div>
              <span className='ml-auto text-xs text-gray bg-light_gray rounded-md px-2 py-1 whitespace-nowrap dark:text-gray-400 dark:bg-dk-raised'>
                {transaction.seats} {transaction.seats === 1 ? 'asiento' : 'asientos'}
              </span>
            </div>

            <div className='flex flex-wrap items-center gap-x-3 gap-y-2 text-sm'>
              {transaction.driver && (
                <span
                  className='inline-flex items-center gap-2 text-primary cursor-pointer hover:bg-red-50 font-medium border border-primary/30 rounded-lg px-2.5 py-1.5 transition-colors dark:hover:bg-red-500/10'
                  onClick={() => {
                    const url = `${window.location.origin}?navigation=UserSearch&user_id=${encodeURIComponent(transaction.driver.user_id)}`
                    window.open(url, '_blank')
                  }}
                >
                  <FontAwesomeIcon icon={faUser} className='text-xs' />{' '}
                  {transaction.driver.first_name}
                  <FontAwesomeIcon icon={faChevronRight} className='text-[10px]' />
                </span>
              )}
              {transaction.lobby_id && (
                <span
                  className='inline-flex items-center gap-2 text-primary cursor-pointer hover:bg-red-50 font-medium border border-primary/30 rounded-lg px-2.5 py-1.5 transition-colors dark:hover:bg-red-500/10'
                  onClick={() => onSelectLobby(transaction.lobby_id)}
                >
                  <FontAwesomeIcon icon={faCar} className='text-xs' /> Ver viaje{' '}
                  <FontAwesomeIcon icon={faChevronRight} className='text-[10px]' />
                </span>
              )}
              {isPending && (
                <>
                  {status === 'pending' && (
                    <button
                      onClick={() => setJoinCompanionAlert(true)}
                      className='inline-flex items-center gap-2 text-white bg-primary cursor-pointer hover:bg-primary/80 font-medium rounded-lg px-2.5 py-1.5 transition-colors'
                    >
                      <FontAwesomeIcon icon={faUser} className='text-xs' />
                      Unir Acompañante
                    </button>
                  )}
                  <button
                    onClick={copyPaymentLink}
                    className='inline-flex items-center gap-2 text-primary cursor-pointer hover:bg-red-50 font-medium border border-primary/30 rounded-lg px-2.5 py-1.5 transition-colors dark:hover:bg-red-500/10'
                  >
                    <ContentCopyRounded className='text-sm' />
                    Copiar Link
                  </button>
                </>
              )}
              {canRefund && (
                <button
                  onClick={() => setRefundDialog(true)}
                  className='inline-flex items-center gap-2 text-white bg-primary cursor-pointer hover:bg-primary/80 font-medium rounded-lg px-2.5 py-1.5 transition-colors'
                >
                  Reembolsar
                </button>
              )}
              {canResolveRefund && (
                <button
                  onClick={() => setResolveDialog(true)}
                  className='inline-flex items-center gap-2 text-white bg-primary cursor-pointer hover:bg-primary/80 font-medium rounded-lg px-2.5 py-1.5 transition-colors'
                >
                  Resolver reembolso
                </button>
              )}
              {canRefundRemainder && (
                <button
                  onClick={() => setRemainderDialog(true)}
                  className='inline-flex items-center gap-2 text-white bg-primary cursor-pointer hover:bg-primary/80 font-medium rounded-lg px-2.5 py-1.5 transition-colors'
                >
                  Reembolsar restante
                </button>
              )}
            </div>

            <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray dark:text-gray-400'>
              <span>{purchaseDate ? formatShortDate(purchaseDate) : 'Fecha no disponible'}</span>
              {refundDate && <span>Reembolso: {formatShortDate(refundDate)}</span>}
            </div>

            <p className='text-[11px] text-gray/60 truncate dark:text-gray-400'>{transaction._id}</p>
          </div>
        </div>
      </div>

      <RefundDialog
        open={refundDialog}
        transactionId={transaction._id}
        details={[
          ['Viaje', [transaction.origin, transaction.destination].filter(Boolean).join(' → ')],
          ['Total cobrado', formatMoney(transaction.price, transaction.currency)],
          ['Pago', paymentMethodLabel(transaction, walletShare)]
        ]}
        alternativePayment={Boolean(transaction.alternative_payment)}
        walletShare={walletShare}
        onClose={() => setRefundDialog(false)}
        onRefunded={result => {
          setRefundDialog(false)
          setStatus(result?.status || 'refunded')
          setRefund(result?.refund || { refund_reason: 'admin_manual_refund' })
        }}
      />

      <ResolveRefundDialog
        open={resolveDialog}
        transactionId={transaction._id}
        details={[
          ['Viaje', [transaction.origin, transaction.destination].filter(Boolean).join(' → ')],
          ['A devolver', formatMoney(refundInfo?.amount, refundInfo?.currency)],
          ['Pago', paymentMethodLabel(transaction, walletShare)]
        ]}
        onClose={() => setResolveDialog(false)}
        onResolved={update => {
          if (update.status) setStatus(update.status)
          setRefund(refundAfterResolve(update, transactionWithStatus))
        }}
      />

      <RefundRemainderDialog
        open={remainderDialog}
        transactionId={transaction._id}
        details={[
          ['Viaje', [transaction.origin, transaction.destination].filter(Boolean).join(' → ')],
          ['Total cobrado', formatMoney(transaction.price, transaction.currency)],
          ['Ya devuelto', formatMoney(refundInfo?.amount, refundInfo?.currency)]
        ]}
        onClose={() => setRemainderDialog(false)}
        onRefunded={update => {
          if (update.status) setStatus(update.status)
          setRefund(refundAfterRemainder(update, transactionWithStatus))
        }}
      />

      {isPending && joinCompanionAlert && (
        <Alert
          onlyConfirm
          onSubmit={() => setJoinCompanion(true)}
          onClose={() => {
            if (!joinCompanion) setJoinCompanionAlert(false)
          }}
          visible={joinCompanionAlert}
          disabled={joinCompanion}
        >
          {joinCompanion ? (
            <div className='flex justify-center items-center'>
              <JoinCompanion
                transaction={transaction}
                setJoinCompanion={setJoinCompanion}
                setJoinCompanionAlert={setJoinCompanionAlert}
                setStatus={setStatus}
                paymentMethod={paymentMethod}
              />
            </div>
          ) : (
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100'>Unir Acompañante</h3>
              <p className='text-sm text-gray-600 mb-4 dark:text-gray-300'>
                Selecciona el método de pago para aprobar esta transacción
              </p>
              <Select
                value={paymentMethod}
                size='small'
                placeholder='Seleccionar método de pago'
                onChange={evt => setPaymentMethod(evt.target.value)}
                fullWidth
              >
                <MenuItem value='wire-transfer'>Transferencia Bancaria</MenuItem>
                <MenuItem value='deposit'>Depósito</MenuItem>
              </Select>
            </div>
          )}
        </Alert>
      )}
    </>
  )
}
